import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Activity,
  BarChart3,
  GripHorizontal,
  KanbanSquare,
  Layers,
  PieChart,
  Table2,
  Trash2,
  TrendingUp,
  Type,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { useDeleteElement } from '../hooks/use-delete-element';
import { useElementData, useElementDataPaginated } from '../hooks/use-report-data';
import { DateFilter, ReportElement, ReportElementType } from '../types';
import { buildChartData } from '../utils/chart-data';
import { CHART_COLORS, formatMeasureValue } from '../utils/format-measure';
import { ElementAreaChart } from './element-area-chart';
import { ElementBarChart } from './element-bar-chart';
import { ElementKpiCard } from './element-kpi-card';
import { ElementLineChart } from './element-line-chart';
import { ElementPieChart } from './element-pie-chart';
import { ElementTable } from './element-table';
import { ElementTextBlock } from './element-text-block';

export const ELEMENT_ICONS: Record<string, React.ElementType> = {
  [ReportElementType.TABLE]: Table2,
  [ReportElementType.KPI_CARD]: KanbanSquare,
  [ReportElementType.TEXT_BLOCK]: Type,
  [ReportElementType.BAR_CHART]: BarChart3,
  [ReportElementType.LINE_CHART]: TrendingUp,
  [ReportElementType.AREA_CHART]: Activity,
  [ReportElementType.PIE_CHART]: PieChart,
};

const CHART_SERIES_TYPES = new Set([
  ReportElementType.BAR_CHART,
  ReportElementType.LINE_CHART,
  ReportElementType.AREA_CHART,
  ReportElementType.PIE_CHART,
]);

interface ReportElementCardProps {
  element: ReportElement;
  reportId: string;
  dateFilter: DateFilter;
  isEditing?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  /** When set, the element shrinks to content height up to this pixel cap, then scrolls. */
  maxHeight?: number;
}

export const ReportElementCard = ({
  element,
  reportId,
  dateFilter,
  isEditing,
  isSelected,
  onSelect,
  maxHeight,
}: ReportElementCardProps) => {
  const { t } = useTranslation('authenticated.reports');
  const { mutate: deleteElement, isPending: isDeleting } = useDeleteElement();

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(52);
  const [showSeriesPanel, setShowSeriesPanel] = useState(false);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const isChartType = CHART_SERIES_TYPES.has(element.type);

  const handleToggleSeries = (key: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const el = headerRef.current;
    if (!el || maxHeight == null) return;
    const observer = new ResizeObserver(() => setHeaderHeight(el.offsetHeight));
    observer.observe(el);
    setHeaderHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, [maxHeight]);

  const hasQueryConfig = !!element.reportType;
  const isTable = element.type === ReportElementType.TABLE;
  const { data, isLoading } = useElementData(
    reportId,
    element.id,
    dateFilter,
    hasQueryConfig && !isTable
  );

  const seriesEntries = useMemo((): Array<{
    key: string;
    label: string;
    color: string;
    value?: string;
  }> => {
    if (!data || !isChartType) return [];
    if (element.type === ReportElementType.PIE_CHART) {
      const { rows } = buildChartData(data);
      return rows.map((row, i) => ({
        key: row.__label as string,
        label: row.__label as string,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }));
    }
    const { measures, rows } = buildChartData(data);
    if (measures.length === 1) {
      const m = measures[0];
      return rows.map(row => ({
        key: row.__label as string,
        label: row.__label as string,
        color: '',
        value: formatMeasureValue(row[m.key] as number, m.type),
      }));
    }
    return measures.map((m, i) => ({
      key: m.key,
      label: m.label,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
  }, [data, isChartType, element.type]);
  const {
    data: tableData,
    isLoading: isTableLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useElementDataPaginated(reportId, element.id, dateFilter, hasQueryConfig && isTable);

  const Icon = ELEMENT_ICONS[element.type] ?? BarChart3;
  const typeLabel = t(`element.${element.type}`);
  const customTitle = element.config.title as string | undefined;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteElement({ reportId, elementId: element.id });
  };

  const renderContent = () => {
    const loading = isTable ? isTableLoading : isLoading;
    if (loading) {
      return <Skeleton className="h-24 w-full" />;
    }

    const activeData = isTable ? tableData : data;
    if (!activeData) {
      return <p className="text-sm text-muted-foreground">{t('element.noDataLoaded')}</p>;
    }

    switch (element.type) {
      case ReportElementType.TABLE:
        return (
          <ElementTable
            data={activeData}
            maxContentHeight={maxHeight != null ? maxHeight - headerHeight : undefined}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
          />
        );
      case ReportElementType.KPI_CARD:
        return <ElementKpiCard data={activeData} />;
      case ReportElementType.TEXT_BLOCK:
        return <ElementTextBlock config={element.config} />;
      case ReportElementType.BAR_CHART:
        return (
          <ElementBarChart
            data={activeData}
            hiddenSeries={hiddenSeries}
            showAverageLine={element.config.showAverageLine as boolean | undefined}
            targets={element.config.targets as Array<{ label: string; value: number }> | undefined}
            zones={
              element.config.zones as
                | Array<{ from?: number; to?: number; color: string; label?: string }>
                | undefined
            }
          />
        );
      case ReportElementType.LINE_CHART:
        return (
          <ElementLineChart
            data={activeData}
            hiddenSeries={hiddenSeries}
            showAverageLine={element.config.showAverageLine as boolean | undefined}
            targets={element.config.targets as Array<{ label: string; value: number }> | undefined}
            zones={
              element.config.zones as
                | Array<{ from?: number; to?: number; color: string; label?: string }>
                | undefined
            }
          />
        );
      case ReportElementType.AREA_CHART:
        return (
          <ElementAreaChart
            data={activeData}
            hiddenSeries={hiddenSeries}
            showAverageLine={element.config.showAverageLine as boolean | undefined}
            targets={element.config.targets as Array<{ label: string; value: number }> | undefined}
            zones={
              element.config.zones as
                | Array<{ from?: number; to?: number; color: string; label?: string }>
                | undefined
            }
          />
        );
      case ReportElementType.PIE_CHART:
        return <ElementPieChart data={activeData} hiddenSeries={hiddenSeries} />;
      default:
        return (
          <p className="text-sm text-muted-foreground">
            {t('element.previewNotAvailable', { type: typeLabel })}
          </p>
        );
    }
  };

  return (
    <Card
      className={cn(
        'flex flex-col transition-shadow',
        maxHeight == null ? 'h-full' : 'h-auto',
        isSelected && 'ring-2 ring-primary ring-offset-1'
      )}
      onClick={onSelect}
    >
      <CardHeader
        ref={headerRef}
        className={cn(
          'element-drag-handle flex shrink-0 flex-row items-center justify-between p-3 pb-2',
          isEditing && 'cursor-grab active:cursor-grabbing'
        )}
      >
        <div className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
          {isEditing && (
            <GripHorizontal className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
          )}
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{customTitle || typeLabel}</span>
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          {isEditing && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {!isEditing && isChartType && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground"
              onClick={e => {
                e.stopPropagation();
                setShowSeriesPanel(s => !s);
              }}
            >
              <Layers className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent
        className={cn(
          'flex flex-row gap-0 p-0',
          maxHeight == null ? 'flex-1 min-h-0 overflow-hidden' : 'min-h-0'
        )}
      >
        <div className={cn('min-w-0 flex-1 p-3 pt-0', maxHeight == null ? 'overflow-hidden' : '')}>
          {renderContent()}
        </div>
        {showSeriesPanel && isChartType && seriesEntries.length > 0 && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            className="flex w-44 shrink-0 flex-col overflow-hidden border-l bg-background"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('element.series')}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-muted-foreground"
                onClick={() => setShowSeriesPanel(false)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {seriesEntries.map(entry => {
                const isHidden = hiddenSeries.has(entry.key);
                return (
                  <button
                    key={entry.key}
                    type="button"
                    onClick={() => handleToggleSeries(entry.key)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-muted',
                      isHidden && 'opacity-40'
                    )}
                  >
                    {entry.color ? (
                      <span
                        className="h-2 w-2 shrink-0 rounded-sm"
                        style={{ backgroundColor: entry.color }}
                      />
                    ) : null}
                    <span className={cn('min-w-0 flex-1 truncate', isHidden && 'line-through')}>
                      {entry.label}
                    </span>
                    {entry.value != null && (
                      <span className="max-w-[80px] shrink-0 truncate tabular-nums text-muted-foreground">
                        {entry.value}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
